import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";

const TYPES = [["all", "All"], ["agent", "Agents"], ["lender", "Lenders"], ["title", "Title"], ["client", "Clients"], ["vendor", "Vendors"]];
const TONE = { agent: "green", lender: "amber", title: "orange", client: "emerald", vendor: "gray" };
const inp = "w-full px-3 py-2 bg-white border border-[#E2E6F0] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none";
const lbl = "text-[11px] tracking-[0.12em] uppercase font-semibold text-[#555] block mb-1";
const EMPTY = { name: "", type: "agent", email: "", phone: "", company: "", headshot: "", owner_id: "" };

export default function People() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isManager = me.role === "admin" || me.can_manage_users;
  const [people, setPeople] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => setPeople((await api.get("/people")).data), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isManager) api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}); }, [isManager]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, owner_id: me.id }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };
  const reassign = async (p, owner_id) => {
    try { await api.put(`/people/${p.id}`, { owner_id }); toast.success("Owner updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/people/${editing.id}`, form); toast.success("Person updated"); }
      else { await api.post("/people", form); toast.success("Person added"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (p) => { if (!window.confirm(`Remove ${p.name}?`)) return; try { await api.delete(`/people/${p.id}`); toast.success("Removed"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const filtered = people.filter((p) => (filter === "all" || p.type === filter) && `${p.name} ${p.company} ${p.email}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="people-page">
      <PageHeader
        title="People"
        subtitle={isManager
          ? "Everyone your team works with — agents, lenders, title reps, clients and vendors. As a manager you see every contact and can reassign who owns each one."
          : "Your contacts — the people and leads that came in through you. Enter someone once and reuse them across every tool."}
        action={<button data-testid="add-person-button" onClick={openNew} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F7941D] text-white font-semibold hover:bg-[#E08312] transition-colors duration-200"><Plus className="w-4 h-4" /> Add Person</button>}
      />

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
        <div className="flex flex-wrap gap-1">
          {TYPES.map(([k, label]) => (
            <button key={k} data-testid={`people-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${filter === k ? "bg-[#1F3C88] text-white" : "bg-white border border-[#E2E6F0] text-[#555] hover:bg-[#F8FAFE]"}`}>{label}</button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <input data-testid="people-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, email…" className="w-full pl-9 pr-3 py-2 bg-white border border-[#E2E6F0] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:outline-none" />
        </div>
      </div>

      <div className="bg-white border border-[#E2E6F0] rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E2E6F0] text-left text-xs tracking-[0.12em] uppercase text-[#555]">
            <th className="px-6 py-3 font-semibold">Name</th><th className="px-6 py-3 font-semibold">Type</th>
            <th className="px-6 py-3 font-semibold">Company</th><th className="px-6 py-3 font-semibold">Contact</th>
            {isManager && <th className="px-6 py-3 font-semibold">Owner</th>}
            <th className="px-6 py-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} data-testid={`person-row-${p.id}`} onClick={() => navigate(`/people/${p.id}`)} className="border-b border-[#F0F2F8] hover:bg-[#F8FAFE] cursor-pointer">
                <td className="px-6 py-3"><div className="flex items-center gap-3">{p.headshot ? <img src={p.headshot} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-[#EAF0FB]" />}<span className="font-medium text-[#111]">{p.name}</span></div></td>
                <td className="px-6 py-3"><StatusBadge tone={TONE[p.type] || "gray"}>{p.type}</StatusBadge></td>
                <td className="px-6 py-3 text-[#555]">{p.company || "—"}</td>
                <td className="px-6 py-3 text-[#555]">{p.email || p.phone ? <span>{p.email}<div className="text-xs">{p.phone}</div></span> : "—"}</td>
                {isManager && <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                  <select data-testid={`owner-select-${p.id}`} value={p.owner_id || ""} onChange={(e) => reassign(p, e.target.value)} className="px-2 py-1.5 bg-white border border-[#E2E6F0] rounded-sm text-xs focus:ring-2 focus:ring-[#F7941D] focus:outline-none">
                    <option value="">Unassigned</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </td>}
                <td className="px-6 py-3"><div className="flex items-center justify-end gap-1">
                  <button data-testid={`edit-person-${p.id}`} onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="p-2 rounded-sm text-[#555] hover:bg-[#F0F2F8] hover:text-[#1F3C88]"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-person-${p.id}`} onClick={(e) => { e.stopPropagation(); remove(p); }} className="p-2 rounded-sm text-[#555] hover:bg-[#F6E4E4] hover:text-[#992B2B]"><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={isManager ? 6 : 5} className="px-6 py-10 text-center text-[#999]">No people found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Person" : "Add Person"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="person-form">
            <div className="grid grid-cols-2 gap-3">
              <div><span className={lbl}>Name</span><input required data-testid="person-name-input" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><span className={lbl}>Type</span><select data-testid="person-type-select" className={inp} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>{TYPES.filter(([k]) => k !== "all").map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
              <div><span className={lbl}>Email</span><input className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><span className={lbl}>Phone</span><input className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><span className={lbl}>Company</span><input className={inp} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div><span className={lbl}>Headshot URL</span><input className={inp} value={form.headshot} onChange={(e) => setForm({ ...form, headshot: e.target.value })} /></div>
              {isManager && <div className="col-span-2"><span className={lbl}>Owner</span><select data-testid="person-owner-select" className={inp} value={form.owner_id || ""} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}><option value="">Unassigned</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></div>}
            </div>
            <button data-testid="person-submit-button" className="w-full py-3 rounded-full bg-[#1F3C88] text-white font-semibold hover:bg-[#152A61]">{editing ? "Save" : "Add Person"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
